import { useState, useEffect, useCallback } from "react";
import { useAuth } from "react-oidc-context";
import { getRealm, getRestServer } from "../AuthProvider";
import FullscreenLoader from '../components/FullScreenLoader';
import Navbar from "../components/Navbar";
import { Card, CardContent, Typography, Stack, Button, Box } from "@mui/material";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";

import { JsonForms } from "@jsonforms/react";
import { materialCells, materialRenderers } from "@jsonforms/material-renderers";

import {
  dealerOnConnectSchema,
  dealerOnConnectUiSchema,
  dealerOnDetailsSchema,
  dealerOnDetailsUiSchema
} from "../schema/dealerOn";

import '../css/dealerOn.css';

export default function DealerOn() {
  const auth = useAuth();
  const [serverUrl] = useState(getRestServer());
  const [realm] = useState(getRealm());
  const [loading, setLoading] = useState(true);

  const [connected, setConnected] = useState(false);
  const [apiKey, setApiKey] = useState("");
  const [connectData, setConnectData] = useState({ password: "", apiKey: "" });

  const [connectError, setConnectError] = useState("");

  const [detailsData, setDetailsData] = useState({
    name: "",
    email: "",
    domain: "",
    dealerOnId: "",
    type: "Percentage",
    amount: "0"
  });

  const handleDetails = async () => {
    setLoading(true);
    const payload = {
      merchantUniqueId: detailsData.dealerOnId,
      domainUrl: detailsData.domain,
      metadata: {
        isPercentage: detailsData.type === 'Percentage',
        isPercentageValue: Number(detailsData.amount).toFixed(2)
      }
    };

    const res = await fetch(`${serverUrl}/api/${realm}/dealerOn/client/set`, {
      method: "POST",
      headers: { Authorization: `Bearer ${auth.user.id_token}` },
      body: JSON.stringify(payload)
    });

    if (res.status === 401) {
      auth.signinRedirect();
    }

    setLoading(false);
  };

  const handleConnect = async (password) => {
    try {
      setLoading(true);
      const data = JSON.parse(atob(password));

      if (!data.clientID) {
        setConnectError("Missing required field: clientID");
        setConnectData({ password: "", apiKey: "" });
        setLoading(false);
        return;
      }

      if (!data.clientSecret) {
        setConnectError("Missing required field: clientSecret");
        setConnectData({ password: "", apiKey: "" });
        setLoading(false);
        return;
      }

      if (!data.apiKey) {
        setConnectError("Missing required field: apiKey");
        setConnectData({ password: "", apiKey: "" });
        setLoading(false);
        return;
      }

      const res = await fetch(`${serverUrl}/api/${realm}/dealerOn/client/connect`, {
        method: "POST",
        headers: { Authorization: `Bearer ${auth.user.id_token}` },
        body: JSON.stringify(data)
      });

      if (res.status === 200) {
        const { redirectTo } = await res.json();
        return (window.location.href = redirectTo);
      } else if (res.status === 401) {
        auth.signinRedirect();
      }

      setLoading(false);
    } catch (e) {
      console.error(e);
      setConnectData({ password: "", apiKey: "" });
      setConnectError("Malformed Base64 format.");
      setLoading(false);
    }
  };

  const handleDisconnect = async () => {
    try {
      setLoading(true);
      const res = await fetch(`${serverUrl}/api/${realm}/dealerOn/client/disconnect`, {
        method: "GET",
        headers: { Authorization: `Bearer ${auth.user.id_token}` }
      });

      if (res.status === 200) {
        const { redirectTo } = await res.json();
        return (window.location.href = redirectTo);
      } else if (res.status === 401) {
        auth.signinRedirect();
      }

      setLoading(false);
    } catch (e) {
      console.error(e);
      setLoading(false);
    }
  };

  const loadDetails = useCallback(async () => {
    try {
      const res = await fetch(`${serverUrl}/api/${realm}/dealerOn/client/get`, {
        method: "GET",
        headers: { Authorization: `Bearer ${auth.user.id_token}` }
      });

      if (res.status === 200) {
        const data = await res.json();

        setDetailsData({
          name: data.user.name,
          email: data.user.email,
          domain: data.domainUrl,
          dealerOnId: data.merchantUniqueId,
          type: data.metadata?.isPercentage ? "Percentage" : "Absolute",
          amount: data.metadata?.isPercentageValue || "0"
        });

        setConnectData({ password: "", apiKey: data.apiKey });
        setConnected(data.connected || false);
        setApiKey(data.apiKey);
      } else if (res.status === 401) {
        auth.signinRedirect();
      }
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  }, [auth, realm, serverUrl]);

  useEffect(() => {
    if (auth.isAuthenticated) {
      loadDetails();
    }
  }, [auth, loadDetails]);

  if (!auth.isAuthenticated) return null;
  if (loading) return <FullscreenLoader text="Loading data..." />;

  return (
    <>
      <Navbar />
      <Box sx={{ display: "flex", justifyContent: "center", mt: 4 }}>
        <Stack spacing={4}>
          {/* Connect Form */}
          <Card sx={{ width: 400 }}>
            <CardContent>
              <Typography variant="h6">Client Connect</Typography>
              <Stack spacing={2}>
                {!connected ? (
                  <>
                    <JsonForms
                      schema={dealerOnConnectSchema}
                      uischema={dealerOnConnectUiSchema}
                      data={connectData}
                      renderers={materialRenderers}
                      cells={materialCells}
                      onChange={({ data }) => setConnectData(data)}
                    />
                    {connectError && (
                      <Typography color="error" variant="body2" style={{ marginTop: '0px' }}>
                        {connectError}
                      </Typography>
                    )}
                    <Button
                      variant="contained"
                      onClick={() => handleConnect(connectData.password)}
                    >
                      Connect
                    </Button>
                  </>
                ) : (
                  <>
                    <Typography
                      color="success.main"
                      display="flex"
                      alignItems="center"
                      gap={1}
                    >
                      Client connected <CheckCircleIcon color="success" />
                    </Typography>
                    <Typography variant="subtitle2" color="text.secondary">
                      <strong>API key</strong>: {apiKey}
                    </Typography>
                    <Button
                      variant="outlined"
                      color="error"
                      onClick={handleDisconnect}
                    >
                      Disconnect
                    </Button>
                  </>
                )}
              </Stack>
            </CardContent>
          </Card>

          {/* Details Form */}
          <Card sx={{ width: 400 }}>
            <CardContent>
              <Typography variant="h6">Details</Typography>
              <Stack spacing={2}>
                <JsonForms
                  schema={dealerOnDetailsSchema}
                  uischema={dealerOnDetailsUiSchema}
                  data={detailsData}
                  renderers={materialRenderers}
                  cells={materialCells}
                  onChange={({ data }) => {
                    let amount = data.amount?.replace(",", ".") || "";
                    if (amount === ".") {
                      amount = "0.";
                    }
          
                    const regex = /^\d{0,10}(\.\d{0,2})?$/;
          
                    if (regex.test(amount)) {
                      if (
                        amount !== detailsData.amount || JSON.stringify({ ...data, amount: amount }) !== JSON.stringify(detailsData)) {
                          setDetailsData({ ...data, amount: amount });
                        }
                    } else {
                      setDetailsData({ ...data, amount: detailsData.amount });
                    }
                  }}
                />
                <Button
                  variant="contained"
                  onClick={handleDetails}
                  disabled={
                    !detailsData.amount ||
                    !detailsData.dealerOnId ||
                    !detailsData.domain
                  }
                >
                  Submit
                </Button>
              </Stack>
            </CardContent>
          </Card>
        </Stack>
      </Box>
    </>
  );
}